import { useState, useRef } from "react";
import { BarChart, Bar, XAxis, YAxis, LabelList, ResponsiveContainer } from "recharts";
import ChartLegend from "./ChartLegend";

const LABEL_FONT = "13px sans-serif";
const LABEL_PADDING = 5;

const getTextWidth = (() => {
  let canvas;
  return (text) => {
    if (typeof document === "undefined") return 0;
    if (!canvas) canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    ctx.font = LABEL_FONT;
    return ctx.measureText(text).width;
  };
})();

const COLORS = {
  digital: "#0a80f5",
  analogical: "#00994f",
  failed: "#888888",
};

const LEGEND_ITEMS = [
  { color: COLORS.digital, label: "Digitale" },
  { color: COLORS.analogical, label: "Analogico" },
  { color: COLORS.failed, label: "Non consegnate" },
];

const SegmentLabel = ({ x, y, width, value, count, fill }) => {
  if (!value) return null;
  const fullText = `${value.toFixed(1)}% (${count})`;
  const cx = x + width / 2;
  if (width < getTextWidth(fullText) + LABEL_PADDING) return null;
  const label = fullText;
  return (
    <g pointerEvents="none">
      <text x={cx} y={y + 20} fill={fill} fontSize={13} textAnchor="middle" dominantBaseline="middle">
        {label}
      </text>
    </g>
  );
};

const DualBarChart = ({ width, height, data, hideName, showTotals, left, showLegend = false }) => {
  const [tooltipData, setTooltipData] = useState(null);
  const containerRef = useRef(null);

  const pinTooltip = (data, segmentData, event) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setTooltipData({
      ...segmentData,
      x: event.clientX - rect.left + 12,
      y: event.clientY - rect.top - 36,
    });
  };

  const percentData = data.map((item) => {
    const sent = item.sent ?? 0;
    const digital = item.digitalSuccess ?? 0;
    const analogical = item.analogicalSuccess ?? 0;
    const failed = Math.max(sent - digital - analogical, 0);

    return {
      ...item,
      digitalP: sent > 0 ? (digital / sent) * 100 : 0,
      analogicalP: sent > 0 ? (analogical / sent) * 100 : 0,
      failedP: sent > 0 ? (failed / sent) * 100 : 0,
    };
  });

  return (
    <div style={{ position: "relative" }} ref={containerRef}>
      {tooltipData && (
        <div style={{
          position: "absolute", top: tooltipData.y, left: tooltipData.x,
          background: "#fff", border: `1px solid ${tooltipData.color}`,
          padding: "4px 8px", borderRadius: 4, fontSize: 12,
          pointerEvents: "none", zIndex: 10, lineHeight: 1.4, whiteSpace: "nowrap",
        }}>
          <div style={{ color: tooltipData.color, fontWeight: "bold" }}>{tooltipData.label}</div>
          <div>{tooltipData.value.toFixed(1)}% ({tooltipData.count})</div>
        </div>
      )}
      <ResponsiveContainer width={width} minHeight={height}>
        <BarChart
          layout="vertical"
          data={percentData}
          margin={{ top: 10, right: showTotals ? 100 : 20, left, bottom: 20 }}
        >
          <XAxis type="number" domain={[0, 100]} allowDataOverflow hide />
          <YAxis
            type="category"
            dataKey="code"
            hide={hideName}
            width={200}
            dx={-10}
            axisLine={false}
            tickLine={false}
          />

          {/* DIGITAL */}
          <Bar
            dataKey="digitalP" stackId="a" fill={COLORS.digital} barSize={40} stroke="#000" strokeWidth={1}
            isAnimationActive={false} cursor="pointer"
            onMouseEnter={(data, index, event) => pinTooltip(data, { label: "Digitale", value: data.digitalP, count: data.digitalSuccess, color: COLORS.digital }, event)}
            onMouseLeave={() => setTooltipData(null)}
          >
            <LabelList
              dataKey="digitalP"
              content={({ x, y, width, value, index }) => (
                <SegmentLabel x={x} y={y} width={width} value={value} count={percentData[index].digitalSuccess} fill="#fff" />
              )}
            />
          </Bar>

          {/* ANALOGICAL */}
          <Bar
            dataKey="analogicalP" stackId="a" fill={COLORS.analogical} barSize={40} stroke="#000" strokeWidth={1}
            isAnimationActive={false} cursor="pointer"
            onMouseEnter={(data, index, event) => pinTooltip(data, { label: "Analogico", value: data.analogicalP, count: data.analogicalSuccess, color: COLORS.analogical }, event)}
            onMouseLeave={() => setTooltipData(null)}
          >
            <LabelList
              dataKey="analogicalP"
              content={({ x, y, width, value, index }) => (
                <SegmentLabel x={x} y={y} width={width} value={value} count={percentData[index].analogicalSuccess} fill="#fff" />
              )}
            />
            {showTotals && (
              <LabelList
                dataKey="sent"
                content={({ x, y, width, index }) => {
                  const item = percentData[index];
                  if (item.failedP > 0) return null;
                  return (
                    <text x={x + width + 10} y={y + 20} fill="#333" fontSize={14} dominantBaseline="middle">
                      {item.sent}
                    </text>
                  );
                }}
              />
            )}
          </Bar>

          {/* NOT DELIVERED */}
          <Bar
            dataKey="failedP" stackId="a" fill={COLORS.failed} barSize={40} stroke="#000" strokeWidth={1}
            isAnimationActive={false} cursor="pointer"
            onMouseEnter={(data, index, event) => pinTooltip(data, { label: "Non consegnate", value: data.failedP, count: data.sent - data.digitalSuccess - data.analogicalSuccess, color: COLORS.failed }, event)}
            onMouseLeave={() => setTooltipData(null)}
          >
            <LabelList
              dataKey="failedP"
              content={({ x, y, width, value, index }) => (
                <SegmentLabel x={x} y={y} width={width} value={value} count={percentData[index].sent - percentData[index].digitalSuccess - percentData[index].analogicalSuccess} fill="#fff" />
              )}
            />
            {showTotals && (
              <LabelList
                dataKey="sent"
                content={({ x, y, width, index }) => {
                  const item = percentData[index];
                  if (item.failedP === 0) return null;
                  return (
                    <text x={x + width + 10} y={y + 20} fill="#333" fontSize={14} dominantBaseline="middle">
                      {item.sent}
                    </text>
                  );
                }}
              />
            )}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {showLegend && <ChartLegend items={LEGEND_ITEMS} />}
    </div>
  );
};

export default DualBarChart;
